/**
 * Helpers de endereço compartilhados entre páginas (TCA, TCE,
 * TcaEmpregador).
 *
 * Padroniza a concatenação logradouro + numero + complemento +
 * bairro + cidade + uf para manter consistência entre tipos de
 * programa e formulários.
 */

/**
 * Concatena os campos de um endereço em uma única string no padrão
 * usado pelo projeto (vide TcaEmpregador/index.js:2263).
 *
 * Formato: `Logradouro, Numero (Complemento) - Bairro, Cidade/UF`
 *
 * Aceita campos opcionais com fallback (logradouro OU endereco
 * pré-concatenado vindo do backend) e omite separadores quando a
 * parte correspondente está vazia/nula.
 *
 * @param {object|null} dados
 * @returns {string}
 */
export const montarEnderecoConcatenado = (dados) => {
  if (!dados) return "";
  const logradouro = (dados.logradouro || dados.endereco || "").trim();
  const numero = (dados.numero || "").trim();
  const complemento = (dados.complemento || "").trim();
  const bairro = (dados.bairro || "").trim();
  const cidade = (dados.cidade || "").trim();
  const uf = (dados.uf || dados.estado_endereco || "").trim();

  // Primeiro bloco: logradouro + (numero | S/N) + (complemento)
  let blocoRua = "";
  if (logradouro) {
    blocoRua = logradouro;
    if (numero) blocoRua += `, ${numero}`;
    else if (!dados.endereco) blocoRua += ", S/N";
    if (complemento) blocoRua += ` (${complemento})`;
  } else if (numero) {
    blocoRua = `nº ${numero}`;
  }

  // Segundo bloco: bairro (com prefixo `-`)
  let blocoBairro = "";
  if (bairro) blocoBairro = `- ${bairro}`;

  // Terceiro bloco: cidade/uf
  let blocoCidadeUf = "";
  if (cidade && uf) blocoCidadeUf = `${cidade}/${uf}`;
  else if (cidade) blocoCidadeUf = cidade;
  else if (uf) blocoCidadeUf = uf;

  // Composição manual dos blocos:
  //   "<rua> - <bairro>, <cidade>/<uf>"
  // onde:
  //   - entre rua e bairro usa-se " - " (sem vírgula)
  //   - entre bairro e cidade/uf usa-se ", "
  //   - vírgulas e hifens entre blocos ausentes são omitidos
  //   - se apenas a cidade (sem bairro) e a rua existir: "<rua>, <cidade>"
  let resultado = blocoRua;
  if (blocoBairro) {
    // "- Centro" já tem o prefixo "-" — juntamos com " " para "Rua X - Centro".
    if (resultado) resultado += ` ${blocoBairro}`;
    else resultado = blocoBairro.replace(/^-\s*/, "");
  }
  if (blocoCidadeUf) {
    if (resultado) resultado += `, ${blocoCidadeUf}`;
    else resultado = blocoCidadeUf;
  }

  return resultado.replace(/^[,\s]+/, "").trim();
};
